import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Ticket, Loader2, MapPin, Clock, Timer, Minus, Plus,
  Zap, ShieldCheck, QrCode, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { SalesProof, ProviderInfo } from './BookingTrust';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const BADGE_META = {
  instant_confirmation: { icon: Zap, label: 'Instant Confirmation' },
  secure_payment: { icon: ShieldCheck, label: 'Secure Payment' },
  mobile_qr: { icon: QrCode, label: 'Mobile QR Ticket' },
};

export default function EticketBooking() {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ holder_name: '', email: '', phone: '', visit_date: '' });
  const [qty, setQty] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/eticket/public/company/${slug}`)
      .then((r) => setCompany(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (notFound)
    return <Center><p className="text-slate-600" data-testid="company-not-found">Booking page not found.</p></Center>;
  if (!company) return <Center><Loader2 className="w-8 h-8 animate-spin text-[#0C2159]" /></Center>;

  const cur = (company.currency || 'MYR').toUpperCase();
  const rates = company.rates || [];
  const gallery = (company.gallery_enabled && Array.isArray(company.gallery_urls))
    ? company.gallery_urls.filter(Boolean)
    : [];
  const mobileLayout = company.gallery_mobile_layout === 'grid' ? 'grid' : 'carousel';
  const badges = company.trust_badges || {};
  const enabledBadges = Object.entries(badges).filter(([, v]) => v).map(([k]) => k);

  const getQty = (id) => Math.max(0, Number(qty[id]) || 0);
  const bump = (id, d) => setQty((q) => ({ ...q, [id]: Math.max(0, (Number(q[id]) || 0) + d) }));
  const paidPax = rates.reduce((s, r) => s + getQty(r.id), 0);

  // Company-wide Buy X Get Y Free promo (mirrors backend): customer pays for the
  // quantity selected and gets `free` bonus tickets on top for every `buy` paid.
  const promo = company.promo && company.promo.enabled ? company.promo : null;
  const buyQty = promo ? Number(promo.buy_qty) : 0;
  const freeCount = buyQty > 0
    ? rates.reduce((s, r) => s + Math.floor(getQty(r.id) / buyQty) * Number(promo.free_qty), 0)
    : 0;
  const totalTickets = paidPax + freeCount;
  const totalNum = rates.reduce((s, r) => s + getQty(r.id) * Number(r.price), 0);
  const total = totalNum.toFixed(2);
  const totalPax = paidPax;

  const handleBook = async () => {
    setError('');
    if (!form.holder_name || !form.email) { setError('Please enter your name and email.'); return; }
    if (!form.phone || form.phone.replace(/\D/g, '').length < 8) { setError('Please enter a valid WhatsApp phone number.'); return; }
    if (!form.visit_date) { setError('Please select a visit date.'); return; }
    if (totalPax === 0) { setError('Please select at least one ticket.'); return; }
    if (!acceptedTerms) { setError('Please accept the Ticket Buyer Terms to continue.'); return; }
    setSubmitting(true);
    try {
      const items = rates.filter((r) => getQty(r.id) > 0).map((r) => ({ rate_id: r.id, qty: getQty(r.id) }));
      const { data } = await axios.post(`${API}/api/eticket/book`, {
        company_slug: slug,
        holder_name: form.holder_name,
        email: form.email,
        phone: form.phone,
        items,
        visit_date: form.visit_date || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  const heroImg = company.hero_image_url;
  const headline = company.hero_headline || company.name;
  const subtext = company.hero_subtext || company.description;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="eticket-booking-page">
      {/* ─── Hero ─── */}
      <section className="relative w-full bg-slate-800" data-testid="hero-section"
        style={{
          minHeight: 'clamp(340px, 48vw, 460px)',
          backgroundImage: heroImg ? `url(${heroImg})` : undefined,
          backgroundSize: 'cover', backgroundPosition: 'center',
        }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/70" />
        {company.logo_url && (
          <img src={company.logo_url} alt="Logo" data-testid="hero-logo"
            className="absolute top-4 left-4 sm:top-6 sm:left-6 w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover ring-2 ring-white/70 shadow-lg bg-white" />
        )}
        <div className="relative max-w-5xl mx-auto px-5 pt-24 pb-10 sm:py-20 text-white">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-widest uppercase text-cyan-300 bg-white/10 backdrop-blur-sm rounded-full px-2.5 py-1">
            <Ticket className="w-3 h-3" /> StayMii E-Ticket&trade;
          </div>
          <h1 className="text-2xl sm:text-5xl font-bold mt-3 max-w-2xl leading-tight" data-testid="company-name">
            {headline}
          </h1>
          {subtext && (
            <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/90" data-testid="hero-subtext">
              {subtext}
            </p>
          )}
          <SalesProof company={company} />
        </div>
      </section>

      {/* ─── Trust badges strip ─── */}
      {enabledBadges.length > 0 && (
        <section className="bg-white border-b border-slate-200" data-testid="trust-badges-row">
          <div className="max-w-5xl mx-auto px-5 py-3 flex overflow-x-auto sm:overflow-visible sm:flex-wrap items-center sm:justify-start gap-x-5 sm:gap-x-6 gap-y-2 whitespace-nowrap">
            {enabledBadges.map((k) => {
              const meta = BADGE_META[k]; if (!meta) return null;
              const Icon = meta.icon;
              return (
                <div key={k} className="flex-shrink-0 flex items-center gap-1.5 text-xs sm:text-sm text-slate-700" data-testid={`trust-badge-${k}`}>
                  <Icon className="w-4 h-4 text-emerald-600" /> {meta.label}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── Body ─── */}
      <div className="max-w-5xl mx-auto px-5 py-8 grid lg:grid-cols-3 gap-6">
        {/* Left column: info + gallery + tickets */}
        <div className="lg:col-span-2 space-y-6">
          {/* Info strip */}
          {(company.location || company.opening_hours || company.visit_duration) && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-3 gap-4" data-testid="info-strip">
              {company.location && (
                <InfoItem icon={MapPin} label="Location" value={company.location} testid="info-location" />
              )}
              {company.opening_hours && (
                <InfoItem icon={Clock} label="Opening hours" value={company.opening_hours} testid="info-hours" />
              )}
              {company.visit_duration && (
                <InfoItem icon={Timer} label="Visit duration" value={formatVisitDuration(company.visit_duration)} testid="info-duration" />
              )}
            </div>
          )}

          {/* Gallery — mobile-optimized (iter167). Two responsive layouts driven by
              company.gallery_mobile_layout: 'carousel' (default, Klook-style) or 'grid'.
              Desktop (sm+) keeps the existing horizontal thumbnail row unchanged. */}
          {gallery.length > 0 && (
            <div data-testid="gallery-section">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Photos</h2>

              {/* MOBILE (< sm) */}
              <div className="sm:hidden">
                {mobileLayout === 'grid' ? (
                  <div className="grid grid-cols-2 gap-3" data-testid="gallery-mobile-grid">
                    {gallery.map((url, i) => (
                      <button key={i} type="button" onClick={() => setLightboxIdx(i)}
                        data-testid={`gallery-item-${i}`}
                        className="p-0 m-0 block w-full aspect-[4/3] rounded-xl overflow-hidden bg-slate-200 shadow-sm">
                        <img src={url} alt={`Attraction ${i + 1}`} loading="lazy" decoding="async"
                          className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <MobileCarousel gallery={gallery} onOpen={setLightboxIdx} />
                )}
              </div>

              {/* DESKTOP (sm+) — unchanged from before iter167 */}
              <div className="hidden sm:block relative">
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x scroll-smooth"
                  style={{ scrollbarWidth: 'thin' }}>
                  {gallery.map((url, i) => (
                    <button key={i} type="button" onClick={() => setLightboxIdx(i)}
                      data-testid={`gallery-item-desktop-${i}`}
                      className="p-0 m-0 block flex-shrink-0 w-60 aspect-[4/3] rounded-xl overflow-hidden bg-slate-200 shadow-sm hover:shadow-md transition-shadow snap-start">
                      <img src={url} alt={`Attraction ${i + 1}`} loading="lazy" decoding="async"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Promotional / partner banner */}
          {company.booking_banner_enabled && company.booking_banner_url && (
            company.booking_banner_link ? (
              <a href={company.booking_banner_link} target="_blank" rel="noopener noreferrer" data-testid="booking-partner-banner-link" className="block rounded-2xl overflow-hidden border border-slate-200 hover:shadow-md transition-shadow">
                <img src={company.booking_banner_url} alt="Promotional offer" className="w-full h-auto" data-testid="booking-partner-banner" loading="lazy" />
              </a>
            ) : (
              <div className="rounded-2xl overflow-hidden border border-slate-200" data-testid="booking-partner-banner-wrap">
                <img src={company.booking_banner_url} alt="Promotional offer" className="w-full h-auto" data-testid="booking-partner-banner" loading="lazy" />
              </div>
            )
          )}

          {/* Ticket cards */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="rate-list">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Select tickets</h2>
            {promo && (
              <div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5" data-testid="promo-banner">
                <Ticket className="w-4 h-4 text-emerald-600 shrink-0" />
                <p className="text-sm font-semibold text-emerald-800">
                  {promo.name || 'Promotion'} · Buy {promo.buy_qty} Get {promo.free_qty} Free
                  <span className="font-normal text-emerald-700"> — buy {promo.buy_qty}, get {promo.free_qty} extra ticket{Number(promo.free_qty) === 1 ? '' : 's'} free</span>
                </p>
              </div>
            )}
            {rates.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="no-rates">No tickets available yet.</p>
            ) : (
              <div className="space-y-3">
                {rates.map((r) => (
                  <div key={r.id} data-testid={`rate-${r.id}`}
                    className="flex items-center gap-4 rounded-xl border border-slate-200 px-4 py-3 hover:border-[#0C2159]/40 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm">{r.name}</p>
                      {r.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.description}</p>}
                      <p className="text-[#0C2159] font-bold text-lg mt-1">
                        {cur} {Number(r.price).toFixed(2)}
                        <span className="text-xs font-normal text-slate-400"> / pax</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => bump(r.id, -1)} disabled={getQty(r.id) === 0} data-testid={`minus-${r.id}`}
                        className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40">
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-6 text-center font-semibold text-slate-900" data-testid={`qty-${r.id}`}>{getQty(r.id)}</span>
                      <button type="button" onClick={() => bump(r.id, 1)} data-testid={`plus-${r.id}`}
                        className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column: sticky booking form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 lg:sticky lg:top-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Your details</h2>
            <div className="space-y-3">
              <Field label="Full name *"><input data-testid="input-holder-name" className="inp" value={form.holder_name} onChange={set('holder_name')} placeholder="Your name" /></Field>
              <Field label="Email *"><input data-testid="input-email" type="email" className="inp" value={form.email} onChange={set('email')} placeholder="you@email.com" /></Field>
              <Field label="Phone (WhatsApp) *"><input data-testid="input-phone" className="inp" value={form.phone} onChange={set('phone')} placeholder="+60..." /></Field>
              <Field label="Visit date *">
                <input data-testid="input-visit-date" type="date"
                  min={new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10)}
                  className="inp" value={form.visit_date} onChange={set('visit_date')} />
              </Field>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="visit-date-note">
                Your ticket will be valid <strong>only on the selected visit date</strong>.
              </p>
            </div>

            {error && <p className="text-sm text-red-600 mt-3" data-testid="booking-error">{error}</p>}

            <label className="mt-4 flex items-start gap-2 cursor-pointer" data-testid="accept-terms-row">
              <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}
                data-testid="accept-terms-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#0C2159] focus:ring-[#0C2159]" />
              <span className="text-xs text-slate-600 leading-relaxed">
                I have read and accept the{' '}
                <a href="/legal/ticket-buyer-terms" target="_blank" rel="noopener noreferrer"
                  className="text-[#0C2159] underline font-medium" data-testid="ticket-buyer-terms-link">
                  Ticket Buyer Terms
                </a>.
              </span>
            </label>

            <div className="mt-4 border-t pt-4 space-y-1.5">
              {promo && freeCount > 0 && (
                <div className="flex items-center justify-between text-sm font-semibold text-emerald-700" data-testid="booking-free-row">
                  <span>🎁 {promo.name || 'Promotion'} · {freeCount} free ticket{freeCount === 1 ? '' : 's'} added</span>
                  <span>{cur} 0.00</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-sm">
                  {totalTickets} ticket{totalTickets === 1 ? '' : 's'}
                  {freeCount > 0 ? ` (${paidPax} paid + ${freeCount} free)` : ''} · Total
                </span>
                <span className="text-xl font-bold text-[#0C2159]" data-testid="booking-total">{cur} {total}</span>
              </div>
            </div>
            <button onClick={handleBook} disabled={submitting || !acceptedTerms || totalPax === 0} data-testid="book-and-pay-btn"
              className="w-full mt-4 bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Ticket className="w-5 h-5" />}
              {submitting ? 'Redirecting to payment…' : `Book & Pay ${cur} ${total}`}
            </button>
            <p className="text-[11px] text-slate-400 mt-3 text-center flex items-center justify-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Secure checkout powered by Stripe
            </p>
          </div>
        </div>
      </div>

      <ProviderInfo company={company} />

      {/* ─── Footer ─── */}
      <footer className="border-t border-slate-200 bg-white mt-6" data-testid="booking-footer">
        <div className="max-w-5xl mx-auto px-5 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>Powered by <a href="https://staymii.com" target="_blank" rel="noopener noreferrer" className="text-[#0C2159] font-semibold">StayMii&trade;</a></p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {company.footer_privacy_url && (
              <a href={company.footer_privacy_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#0C2159]" data-testid="footer-privacy">Privacy Policy</a>
            )}
            {company.footer_terms_url && (
              <a href={company.footer_terms_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#0C2159]" data-testid="footer-terms">Terms &amp; Conditions</a>
            )}
            {company.support_email && (
              <a href={`mailto:${company.support_email}`} className="hover:text-[#0C2159]" data-testid="footer-email">{company.support_email}</a>
            )}
            {company.support_phone && (
              <span data-testid="footer-phone">{company.support_phone}</span>
            )}
          </div>
        </div>
      </footer>

      {/* Gallery lightbox — swipe left/right, prev/next arrows, keyboard nav */}
      {lightboxIdx !== null && gallery[lightboxIdx] && (
        <Lightbox
          gallery={gallery}
          index={lightboxIdx}
          onChange={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}

      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:.6rem;padding:.6rem .75rem;font-size:.9rem;outline:none}.inp:focus{border-color:#0C2159}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-medium text-slate-600">{label}</span><div className="mt-1">{children}</div></label>
);
const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-slate-50">{children}</div>;

const InfoItem = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-2.5" data-testid={testid}>
    <Icon className="w-4 h-4 text-[#0C2159] mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm text-slate-800 font-medium mt-0.5 break-words">{value}</p>
    </div>
  </div>
);

// If the owner typed a bare number like "2" or "1.5", show "2 hours" / "1 hour".
// If they typed "1-2 hours" or any text, keep it as-is.
function formatVisitDuration(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const num = Number(s);
  if (Number.isFinite(num) && /^\d+(\.\d+)?$/.test(s)) {
    return num === 1 ? '1 hour' : `${num} hours`;
  }
  return s;
}


/** Mobile gallery — full-width snap carousel with position dots. Klook-style. */
function MobileCarousel({ gallery, onOpen }) {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el || !el.firstElementChild) return;
    // Use the first child's actual width + gap so dots stay accurate even when
    // container padding / gap change the effective slide step.
    const first = el.firstElementChild;
    const step = first.getBoundingClientRect().width + 12; // 12 = gap-3
    const idx = Math.round(el.scrollLeft / step);
    if (idx !== active) setActive(Math.max(0, Math.min(gallery.length - 1, idx)));
  };

  return (
    <div className="relative -mx-5" data-testid="gallery-mobile-carousel">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth px-5 gap-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {gallery.map((url, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onOpen(i)}
            data-testid={`gallery-item-${i}`}
            className="p-0 m-0 block flex-shrink-0 basis-full aspect-[4/3] rounded-xl overflow-hidden bg-slate-200 shadow-sm snap-center"
          >
            <img
              src={url}
              alt={`Attraction ${i + 1}`}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
      {/* Position dots */}
      {gallery.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="gallery-mobile-dots">
          {gallery.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === active ? 'w-5 bg-[#0C2159]' : 'w-1.5 bg-slate-300'}`}
              data-testid={`gallery-dot-${i}${i === active ? '-active' : ''}`}
            />
          ))}
        </div>
      )}
      {/* Hide native scrollbar on WebKit — Tailwind's `scrollbar-hide` isn't installed. */}
      <style>{`div[data-testid="gallery-mobile-carousel"] > div::-webkit-scrollbar{display:none}`}</style>
    </div>
  );
}


/** Fullscreen lightbox with prev/next arrows, keyboard navigation and touch swipe. */
function Lightbox({ gallery, index, onChange, onClose }) {
  const touchStartX = useRef(null);
  const total = gallery.length;

  const go = (delta) => {
    const next = (index + delta + total) % total;
    onChange(next);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX ?? start;
    const dx = end - start;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <div
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="gallery-lightbox"
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-in fade-in"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
        aria-label="Close gallery"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Prev */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          data-testid="lightbox-prev"
          className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
          aria-label="Previous photo"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Image */}
      <img
        src={gallery[index]}
        alt={`Attraction ${index + 1} of ${total}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[95vw] object-contain rounded-lg select-none"
        draggable={false}
      />

      {/* Next */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); go(1); }}
          data-testid="lightbox-next"
          className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
          aria-label="Next photo"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Counter */}
      {total > 1 && (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 rounded-full px-3 py-1"
          data-testid="lightbox-counter"
        >
          {index + 1} / {total}
        </div>
      )}
    </div>
  );
}
